import type { ReactNode } from 'react';
import { Icone, type NomeDoIcone } from '@/shared/ui/icones';
import './lista-do-historico.css';

/**
 * A lista do histórico: o que aconteceu, quando e por quem, do mais recente ao
 * mais antigo (#765).
 *
 * É o desenho do histórico da oportunidade, levado para cá quando a segunda
 * tela (o detalhe da organização) pediu o mesmo: uma régua à esquerda liga as
 * entradas, e cada uma diz quando, a frase com o ícone, as mudanças e quem
 * fez. O texto vem pronto de quem usa, porque a frase de cada acontecimento é
 * da tela, que conhece o negócio.
 *
 * ── A entrada nova destaca ────────────────────────────────────────────────
 *
 * Quando a pessoa acabou de salvar, a entrada dela acende em roxo-fraca e
 * apaga em 2 s: é o que a faz achar, numa lista longa, o que acabou de fazer.
 */
export type EntradaDaListaDoHistorico = {
  readonly chave: string;
  /** O instante, em ISO: vai no `dateTime`, para máquina ler. */
  readonly instante: string;
  /** O instante em palavras: "agora", "hoje, 14:32", "14/08/2026". */
  readonly quando: string;
  readonly frase: string;
  readonly icone?: NomeDoIcone;
  /** As mudanças, uma por linha: "Usuários contratados: 5 → 7". */
  readonly detalhes?: readonly string[];
  /** Quem fez, já em frase: "por Ana Ribeiro", "pelo sistema". */
  readonly por: string;
  /** A entrada que acabou de entrar: destaca. */
  readonly nova?: boolean;
};

export type ListaDoHistoricoProps = {
  /** O que a lista é, para o leitor de tela: "Histórico da organização". */
  rotulo: string;
  entradas: readonly EntradaDaListaDoHistorico[];
};

export function ListaDoHistorico({ rotulo, entradas }: ListaDoHistoricoProps): ReactNode {
  return (
    <ol className="lista-do-historico" aria-label={rotulo}>
      {entradas.map((entrada) => (
        <li
          key={entrada.chave}
          className={
            entrada.nova
              ? 'lista-do-historico__item lista-do-historico__item--nova'
              : 'lista-do-historico__item'
          }
        >
          <time className="lista-do-historico__quando" dateTime={entrada.instante}>
            {entrada.quando}
          </time>
          <span className="lista-do-historico__frase">
            {entrada.icone ? (
              <span className="lista-do-historico__icone" aria-hidden="true">
                <Icone nome={entrada.icone} />
              </span>
            ) : null}
            {entrada.frase}
          </span>
          {(entrada.detalhes ?? []).map((detalhe) => (
            <span key={detalhe} className="lista-do-historico__detalhe">
              {detalhe}
            </span>
          ))}
          <span className="lista-do-historico__por">{entrada.por}</span>
        </li>
      ))}
    </ol>
  );
}
